import React, { useEffect, useState } from "react";
import { Eye } from "lucide-react";
import { Modal, Button } from "@/components/ui-kit";
import FilePreview from "@/components/FilePreview";
import { formatQty, formatDate } from "@/lib/format";
import api from "@/lib/api";

/**
 * Iter v34cq (#2) — Daftar Surat Jalan yang tercantum pada satu PO.
 * Dibuka sebagai modal lapis-2 di atas PoDetailModal.
 */
export function PoSjListModal({ open, onClose, po }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!open || !po?.id) return;
    setLoading(true);
    api.get("/surat-jalan", { params: { po_id: po.id } })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [open, po?.id]);

  const totalQty = rows.reduce(
    (s, r) => s + (r.items || []).reduce((ss, it) => ss + (Math.round((Number(it.quantity) || 0) * 100) / 100), 0),
    0,
  );

  return (
    <>
      <Modal open={open} onClose={onClose} level={1} size="xl"
        title={`Surat Jalan pada PO ${po?.po_number || ""}`}>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-4 text-xs bg-stone-50 border border-stone-200 px-3 py-2">
            <span className="text-stone-600">Klien: <b className="text-stone-900">{po?.client_name || "—"}</b></span>
            <span className="text-stone-600">Jumlah SJ: <b className="font-mono-num text-stone-900" data-testid="po-sj-count">{rows.length}</b></span>
            <span className="text-stone-600">Total Qty: <b className="font-mono-num text-stone-900">{formatQty(totalQty)}</b></span>
          </div>

          <div className="overflow-x-auto border border-stone-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-stone-50 border-b border-stone-200 text-left text-[10px] uppercase tracking-wider font-semibold text-stone-600">
                  <th className="py-2 px-3">No. SJ</th>
                  <th className="py-2 px-3">Tanggal</th>
                  <th className="py-2 px-3">Material</th>
                  <th className="py-2 px-3">No. Mobil</th>
                  <th className="py-2 px-3 text-right">Qty</th>
                  <th className="py-2 px-3 text-center">Foto SJ</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} className="py-8 text-center text-stone-400">Memuat…</td></tr>}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={6} className="py-8 text-center text-stone-400">Belum ada Surat Jalan pada PO ini.</td></tr>
                )}
                {!loading && rows.map((r, idx) => (
                  <tr key={r.id} className="border-b border-stone-100 align-top" data-testid={`po-sj-row-${idx}`}>
                    <td className="py-2 px-3 font-mono">{r.sj_number || "—"}</td>
                    <td className="py-2 px-3 font-mono text-stone-600">{formatDate(r.date)}</td>
                    <td className="py-2 px-3">
                      <div className="space-y-0.5">
                        {(r.items || []).map((it, i) => (
                          <div key={i} className="text-stone-700 text-xs">{it.material_name || "—"}</div>
                        ))}
                        {(r.items || []).length === 0 && <span className="text-stone-300">—</span>}
                      </div>
                    </td>
                    <td className="py-2 px-3 font-mono text-stone-600">{r.vehicle_no || "—"}</td>
                    <td className="py-2 px-3 text-right">
                      <div className="space-y-0.5">
                        {(r.items || []).map((it, i) => (
                          <div key={i} className="font-mono-num text-xs">
                            {formatQty(it.quantity || 0)} <span className="text-stone-400">{it.unit || ""}</span>
                            {it.use_qty_billing && (
                              <span className="ml-1 text-amber-700">(tagih {formatQty(it.qty_billing || 0)})</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-center whitespace-nowrap">
                      {r.file_id && (
                        <button onClick={() => setPreview({ id: r.file_id, name: r.file_name, ct: r.file_content_type })}
                          className="inline-flex items-center gap-1 border border-stone-300 px-1.5 py-0.5 text-[11px] hover:bg-blue-50 hover:border-blue-300"
                          title="Foto sebelum kirim" data-testid={`po-sj-photo-before-${idx}`}>
                          <Eye className="h-3 w-3 text-blue-700" /> Sebelum
                        </button>
                      )}
                      {r.photo_after_id && (
                        <button onClick={() => setPreview({ id: r.photo_after_id, name: r.photo_after_name, ct: r.photo_after_content_type || "image/jpeg" })}
                          className="inline-flex items-center gap-1 border border-stone-300 px-1.5 py-0.5 text-[11px] hover:bg-emerald-50 hover:border-emerald-300 ml-1"
                          title="Foto sesudah kirim" data-testid={`po-sj-photo-after-${idx}`}>
                          <Eye className="h-3 w-3 text-emerald-700" /> Sesudah
                        </button>
                      )}
                      {!r.file_id && !r.photo_after_id && <span className="text-stone-300 text-xs">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end pt-2 border-t border-stone-100">
            <Button variant="ghost" onClick={onClose} data-testid="po-sj-list-close">Tutup</Button>
          </div>
        </div>
      </Modal>
      <FilePreview open={!!preview} onClose={() => setPreview(null)} fileId={preview?.id}
        filename={preview?.name} contentType={preview?.ct} level={2} />
    </>
  );
}

